import MaterialTable, { Column, MTableBody } from "material-table";
import React, { useEffect, useMemo, useState } from "react";
import { arrayToLookup, getStickyColumns, localization, modifyDefaultOptions, tableIconsModified } from "../../../helpers/table";
import ProgramBuysFooter from "./ProgramBuysFooter";
import FilterMultiSelect from "../../../components/table/FilterMultiSelect";
import './ExtraBudgetTable.less'
import useAPI from "../../../hooks/useAPI";
import moment from "moment";
import LoadingTable from "../../../components/table/LoadingTable";

const sumFormatter = new Intl.NumberFormat('ru', {style: 'decimal', minimumFractionDigits: 2})

export default function ExtraBudgetTable({year}: {year: number}){
  const api = useAPI()

  const [data, setData] = useState<ExtraBudgetRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const lookups = useMemo<ExtraBudgerLookups>(() => {
    return ['parentName'].reduce((acc, key) => ({...acc, [key]: arrayToLookup(key, data)}), {})
  }, [data]) 

  const stickyColumns = useMemo<Column<ExtraBudgetRow>[]>(() => {
    const columns: Column<ExtraBudgetRow>[] = [
      {
        title: '№ п/п',
        field: 'order',
        width: 80,
        cellStyle: { minWidth: 80 }
      },
      {
        title: 'Вид собственных доходов',
        field: 'type',
        filtering: true,
        width: 250,
        cellStyle: { minWidth: 250 },
        filterCellStyle: { minWidth: 250 }
      },
    ]
    return getStickyColumns(columns, 0)
  }, [])

  useEffect(() => {
    const fetchData = async() => {
      const data: ExtraBudgetData[] = (await api.get(`/reports/budget/funds/internal?year=${year}`)).data
      setData(data.map((row, i) => {
        let incomeRowData: Pick<ExtraBudgetRow, 'incomeItems' | 'incomeNames' | 'incomeNums' | 'incomeComments'> = {
          incomeNames: [],
          incomeNums: [],
          incomeComments: [],
          incomeItems: []
        }
        row.incomes.forEach(income => {
            incomeRowData = {
            incomeNames: [...incomeRowData.incomeNames, income.docName],
            incomeNums: [...incomeRowData.incomeNums, `${income.docNum} от ${moment(new Date(income.docDate)).format('DD.MM.YYYY')}`],
            incomeComments: [...incomeRowData.incomeComments, income.comment],
            incomeItems: [...incomeRowData.incomeItems, income.costItem]
          }
        }, )
        return {
          order: i + 1,
          ...row,
          ...incomeRowData,
          parentName: 'Научно-исследовательская политика и политика в области инноваций и коммерциализации разработок'
        }
      }))
      setIsLoading(false)
    }
    fetchData()
  }, [year])

  return (
    <div className="mt-4">
      <h2 style={{fontSize: 16, color: '#212529', textAlign: 'left'}}>
        Реестр договоров и иных документов, подтверждающих привлечение средств внебюджетных источников на проведение прикладных научных исследований и (или) экспериментальных разработок, полученных от заказчиков (иных внешних партнеров)
      </h2>
      <LoadingTable loadingObject={{ isLoading }}>
        <MaterialTable<ExtraBudgetRow>
          data={data}
          columns={[
            ...stickyColumns,
            {
              title: 'Реквизиты первичных документов, подтверждающие получение доходов',
              field: 'docNum',
              cellStyle: { minWidth: 240 }
            },
            {
              title: 'Краткая характеристика доходов',
              field: 'descr',
              cellStyle: { minWidth: 240 }
            },
            {
              title: 'Сумма доходов, направленная на мероприятия Программы развития, руб.',
              field: 'sum',
              aggregated: true,
              cellStyle: { minWidth: 280 },
              render: rowData => <div>{sumFormatter.format(rowData.sum)}</div>
            } as Column<ExtraBudgetRow>,
            {
              title: 'Наименование документа, подтверждающего выплату денежных средств',
              field: 'incomeNames',
              cellStyle: { minWidth: 240 },
              render: rowData => <div className="d-flex flex-column gap-1">
                {rowData.incomeNames.map(name => <p className="m-0">{name} <br/></p>)}
              </div>,
              customFilterAndSearch: (filterValue, rowData) => rowData.incomeNames.join('').toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())
            },
            {
              title: '№, дата документа, подтверждающего выплату денежных средств',
              field: 'incomeNums',
              cellStyle: { minWidth: 240 },
              render: rowData => <div className="d-flex flex-column gap-1">
                {rowData.incomeNums.map(num => <p className="m-0">{num} <br/></p>)}
              </div>,
              customFilterAndSearch: (filterValue, rowData) => rowData.incomeNums.join('').toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())
            },
            {
              title: 'Статья расходов',
              field: 'incomeItems',
              cellStyle: { minWidth: 240 },
              render: rowData => <div className="d-flex flex-column gap-1">
                {rowData.incomeItems.map(item => <p className="m-0">{item} <br/></p>)}
              </div>,
              customFilterAndSearch: (filterValue, rowData) => rowData.incomeItems.join('').toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())
            },
            {
              title: 'Сумма всего, руб.',
              field: 'incomesSum',
              aggregated: true,
              cellStyle: { minWidth: 150 },
              render: rowData => <div>{sumFormatter.format(rowData.incomesSum)}</div>
            } as Column<ExtraBudgetRow>,
            {
              title: 'Сумма, в том числе за счет собственных средств, направленных на проведение прикладных научных исследований и (или) экспериментальных разработок, руб.',
              field: 'internalFundSum',
              aggregated: true,
              cellStyle: { minWidth: 470 },
              render: rowData => <div>{sumFormatter.format(rowData.internalFundSum)}</div>
            }  as Column<ExtraBudgetRow>,
            {
              title: 'Примечание',
              field: 'incomeComments',
              cellStyle: { minWidth: 300 },
              render: rowData => <div className="d-flex flex-column gap-1">
                {rowData.incomeComments.map(comment => <p className="m-0">{comment} <br/></p>)}
              </div>,
              customFilterAndSearch: (filterValue, rowData) => rowData.incomeComments.join('').toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())
            },
            {
              title: 'Стратегический проект или раздел научно-исследовательской политики Программы развития университета, в рамках реализации которого (ых) поступили денежные средства',
              field: 'parentName',
              lookup: lookups.parentName,
              cellStyle: { minWidth: 500 },
              filterCellStyle: { maxWidth: 500 },
              filterComponent: FilterMultiSelect 
            } as Column<ExtraBudgetRow>,
          ]}
          options={modifyDefaultOptions({
            toolbar: false,
            sorting: false,
            headerStyle: { fontFamily: 'inherit', position: 'sticky', backgroundColor: 'white', zIndex: 10 },
            filterCellStyle: { position: 'sticky', top: 71.5, backgroundColor: 'white' },
            maxBodyHeight: 'calc(100vh - 436px)',
          })}
          components={{
              Body: (props) =>  <>
                  <MTableBody {...props}/>
                  <ProgramBuysFooter {...props} colSpans={{sum: 4}}/>
                </>
              ,
          }}
          localization={localization}
          icons={tableIconsModified}
        />
      </LoadingTable>
    </div>
  )
}

interface ExtraBudgetData{
  type: string,
  descr: string,
  docNum: string,
  incomes: IIncome[],
  incomesSum: number,
  internalFundSum: number,
  sum: number
}

interface IIncome{
  comment: string, 
  costItem: string,
  docDate: string,
  docName: string,
  docNum: string
}

interface ExtraBudgetRow extends ExtraBudgetData{
  order: number,
  incomeNames: string[],
  incomeNums: string[],
  incomeItems: string[],
  incomeComments: string[],
  parentName: string
}

type ExtraBudgerLookups = {
   [key in keyof ExtraBudgetRow]?: {[key: string]: string} | undefined;
};